import { Select } from "antd";
import { useEffect, useMemo, useState } from "react";
import { ReactEditor, useSlate } from "slate-react";
import { Editor, Element, Transforms, Text, Range } from "slate";
import { useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { hasSelection } from "../utils/hasSelection";

interface FontFamilySelectProps {
  disabled?: boolean;
}

export const FontFamilySelect = ({ disabled }: FontFamilySelectProps) => {
  const { t } = useTranslation(I18N_NS.RICH_EDITOR);
  const fontNames = useMemo(
    () => [
      { value: "Default", label: t("Default") },
      { value: "Arial", label: "Arial" },
      { value: "Times New Roman", label: "Times New Roman" },
      { value: "monospace", label: "monospace" },
    ],
    []
  );
  const editor = useSlate();
  const [value, setValue] = useState<string | number>("Default");

  const handleChange = (value) => {
    setValue(value);
    const newProperties = { fontFamily: value === "Default" ? undefined : value };
    if (hasSelection(editor)) {
      // Apply font family to the selected text
      Transforms.setNodes(editor, newProperties, { at: editor.selection, match: (n) => Text.isText(n), split: true });
    }
    if (newProperties.fontFamily) {
      Editor.addMark(editor, "fontFamily", newProperties.fontFamily);
    } else {
      Editor.removeMark(editor, "fontFamily");
    }
    ReactEditor.focus(editor);
  };

  useEffect(() => {
    const selectedNodes = Array.from(
      Editor.nodes(editor, {
        at: editor.selection,
        match: (node) => (Element.isElement(node) || Text.isText(node)) && !!node.fontFamily,
      })
    );

    if (selectedNodes.length) {
      const fontFamily = (selectedNodes[0][0] as Element)?.fontFamily;
      const selectedValue = fontFamily ? fontFamily : "Default";
      if (value !== selectedValue) {
        setValue(selectedValue);
        if (Range.isCollapsed(editor.selection)) {
          if (selectedValue === "Default") {
            Editor.removeMark(editor, "fontFamily");
          } else {
            Editor.addMark(editor, "fontFamily", selectedValue);
          }
        }
      }
    } else if (value !== "Default") {
      Editor.removeMark(editor, "fontFamily");
      setValue("Default");
    }
  }, [editor?.selection]);

  return (
    <div className="d-flex" title={t("Font family")}>
      <FontAwesomeIcon fixedWidth size="lg" icon={["far", "font-case"]} className="mr-sm" />
      <Select
        disabled={disabled}
        value={value}
        size="small"
        style={{ width: 140 }}
        className="mr-1"
        onChange={handleChange}
        optionRender={(option) => <small>{option.label}</small>}
        options={fontNames}
      />
    </div>
  );
};
